// src/App.js
import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import { AuthProvider, useAuth } from "./auth/AuthContext";
import PrivateRoute from "./auth/PrivateRoute";
import MainLayout from "./layouts/MainLayout";

/* ===================== Pages ===================== */
// Websurrogate
import DataTableView from "./components/DataTableView";
import ReportPage from "./components/ReportPage";
import SettingsPage from "./components/SettingsPage";
import CaseDetailPage from "./components/CaseDetailPage";
import CheckCaseStatusPage from "./components/CheckCaseStatusPage";
import Dashboard from "./components/Dashboard";
import NotificationsPage from "./pages/NotificationsPage";

// Legacy
import LegacyHome from "./pages/LegacyHome";
import LegacyDashboard from "./pages/LegacyDashboard";
import LegacyDetailPage from "./pages/LegacyDetailPage";
import FreshLeads from "./pages/FreshLeads";
import FreshLeadDetail from "./pages/FreshLeadDetail";
import ViolationHome from "./pages/violations/ViolationHome";
import ViolationDetail from "./pages/violations/ViolationDetail";
import ViolationDashboard from "./pages/ViolationDashboard";
import ViolationsV2Dashboard from "./pages/violationsV2/ViolationsV2Dashboard";
import ViolationsV2Detail from "./pages/violationsV2/ViolationsV2Detail";
import ViolationsV2Properties from "./pages/violationsV2/ViolationsV2Properties";
import AcrisLegacyDashboard from "./pages/AcrisLegacyDashboard";
import AcrisLegacyLeads from "./pages/AcrisLegacyLeads";
import AcrisLegacyLeadDetail from "./pages/AcrisLegacyLeadDetail";

// Auth
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";


// Toasts
import { Toaster } from "react-hot-toast";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/* =================================================
   Helpers
   ================================================= */
function getHomePathFromMode(mode) {
  const m = String(mode || "").toLowerCase();
  switch (m) {
    case "legacy":
    case "legacy-mode":
      return "/legacy/dashboard";
    case "violation":
      return "/violations/dashboard";
    case "violations-v2":
    case "violation-v2":
    case "violationsv2":
      return "/violations-v2/dashboard";
    case "acris-legacy":
    case "acrislegacy":
      return "/acris-legacy/dashboard";
    case "admin":
      return "/websurrogate/dashboard";
    case "websurrogate":
    default:
      return "/websurrogate/dashboard";
  }
}

function AuthAwareRedirect() {
  let homePath = "/websurrogate/dashboard";

  try {
    const auth = useAuth();
    const mode =
      auth?.mode ??
      auth?.user?.mode ??
      localStorage.getItem("app_mode") ??
      localStorage.getItem("mode") ??
      localStorage.getItem("userMode");

    homePath = getHomePathFromMode(mode);
  } catch (e) {
    homePath = "/websurrogate/dashboard";
  }

  return <Navigate to={homePath} replace />;
}

/* =================================================
   App
   ================================================= */
export default function App() {
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    petitioner_name: "",
    decedent_name: "",
    court: "",
    petition_type: "",
  });

  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* ================= PUBLIC AUTH ROUTES ================= */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify" element={<VerifyEmail />} />
          <Route path="/forgot" element={<ForgotPassword />} />
          <Route path="/reset" element={<ResetPassword />} />

          {/* ================= PROTECTED APP ================= */}
          <Route element={<PrivateRoute />}>
            <Route element={<MainLayout />}>
              {/* Websurrogate */}
              <Route
                path="/websurrogate"
                element={<DataTableView filters={filters} setFilters={setFilters} />}
              />
              <Route path="/websurrogate/dashboard" element={<Dashboard />} />
              <Route path="/websurrogate/report" element={<ReportPage />} />
              <Route path="/websurrogate/settings" element={<SettingsPage />} />
              <Route
                path="/websurrogate/case-details/:caseId/:court/:petition"
                element={<CaseDetailPage />}
              />
              <Route
                path="/websurrogate/check-case-status"
                element={<CheckCaseStatusPage />}
              />
              <Route
                path="/websurrogate/notifications"
                element={<NotificationsPage />}
              />

              {/* Legacy */}
              <Route path="/legacy" element={<LegacyHome />} />
              <Route path="/legacy/list" element={<LegacyHome />} />
              <Route path="/legacy/dashboard" element={<LegacyDashboard />} />
              <Route path="/legacy/:id" element={<LegacyDetailPage />} />
              <Route path="/legacy/fresh-leads" element={<FreshLeads />} />
              <Route
                path="/legacy/fresh-leads/:name"
                element={<FreshLeadDetail />}
              />
              <Route
                path="/legacy/notifications"
                element={<NotificationsPage />}
              />

              {/* Violation */}
              <Route path="/violations" element={<ViolationHome />} />
              <Route path="/violations/dashboard" element={<ViolationDashboard />} />
              <Route path="/violations/bbl/:bbl" element={<ViolationDetail />} />

              {/* Violations V2 */}
              <Route path="/violations-v2/dashboard" element={<ViolationsV2Dashboard />} />
              <Route path="/violations-v2/properties" element={<ViolationsV2Properties />} />
              <Route path="/violations-v2" element={<Navigate to="/violations-v2/properties" replace />} />
              <Route path="/violations-v2/:bbl" element={<ViolationsV2Detail />} />

              {/* ACRIS Legacy (read-only) */}
              <Route path="/acris-legacy/dashboard" element={<AcrisLegacyDashboard />} />
              <Route path="/acris-legacy/leads" element={<AcrisLegacyLeads initialTab="single" lockTab />} />
              <Route path="/acris-legacy/multiple" element={<AcrisLegacyLeads initialTab="multiple" lockTab />} />
              <Route path="/acris-legacy/leads/:id" element={<AcrisLegacyLeadDetail />} />
              <Route path="/acris-legacy" element={<Navigate to="/acris-legacy/dashboard" replace />} />
            </Route>
          </Route>

          {/* ================= CATCH-ALL REDIRECT ================= */}
          <Route path="*" element={<AuthAwareRedirect />} />
        </Routes>
      </Router>

      {/* Toasts */}
      <Toaster position="top-right" toastOptions={{ duration: 2500 }} />
      <ToastContainer />
    </AuthProvider>
  );
}
